import { useField } from 'formik';
import React, { useMemo } from 'react';

import { ComponentProps } from './interfaces';
import * as Styled from './styled';

export const Dropdown: React.FC<ComponentProps> = props => {
  const {
    name,
    label = '',
    ariaLabel = '',
    placeholder = 'Please Choose...',
    disabled = false,
    required = false,
    options,
    allowLong = false,
    labelProps,
    errorProps,
    containerProps,
    selectProps
  } = props;
  const [field, meta] = useField(name);
  const isError = meta.touched && meta.error;
  // The amount of characters an option can have before the dropdown width is increased
  const longDropdownCharactersThreshold = 35;
  const isLong = useMemo(() => {
    return (
      allowLong && options.some(option => option.label.length > longDropdownCharactersThreshold)
    );
  }, [allowLong, options]);

  const memoOptions = useMemo(() => {
    const renderedOptions = options.map((opt, i) => {
      return (
        <Styled.Option {...opt} key={opt.key || `${opt.label}_${i}`}>
          {opt.label}
        </Styled.Option>
      );
    });
    if (!field.value) {
      return (
        <>
          <Styled.OptionPlaceholder hidden>{placeholder}</Styled.OptionPlaceholder>
          {renderedOptions}
        </>
      );
    }
    return renderedOptions;
  }, [options, placeholder, field.value]);

  return (
    <Styled.Container long={isLong} {...containerProps}>
      <Styled.Label htmlFor={name} id={`${name}.label`} {...labelProps}>
        {label}
      </Styled.Label>
      <Styled.SelectWrapper>
        <Styled.Select
          {...field}
          {...meta}
          aria-label={ariaLabel || label}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          aria-disabled={disabled}
          long={isLong}
          aria-invalid={!!isError}
          aria-labelledby={`${name}.label`}
          {...selectProps}>
          {memoOptions}
        </Styled.Select>
      </Styled.SelectWrapper>
      {isError && (
        <Styled.ErrorLabel htmlFor={name} {...errorProps}>
          {meta.error}
        </Styled.ErrorLabel>
      )}
    </Styled.Container>
  );
};

export default Dropdown;
